import { useMemo, useSyncExternalStore } from 'react';

import type { Presigned, Uploader, UploadProgress } from './create-uploader';

export type UploadItemStatus = 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadItem<TResult> {
  id: string;
  file: File;
  status: UploadItemStatus;
  progress: UploadProgress;
  result?: TResult | undefined;
  error?: unknown;
}

export interface UploadQueueOptions<TResult> {
  concurrency?: number;
  onDone?: (result: TResult, item: UploadItem<TResult>) => void;
}

/**
 * Imperative upload queue with an immutable snapshot for React. Keeping the bookkeeping out
 * of component state avoids ref/effect gymnastics and makes it testable without React.
 */
export class UploadQueue<TPresign extends Presigned, TResult> {
  private items: Array<UploadItem<TResult>> = [];
  private readonly controllers = new Map<string, AbortController>();
  private readonly listeners = new Set<() => void>();
  private readonly pending: string[] = [];
  private active = 0;

  constructor(
    private readonly uploader: Uploader<TPresign, TResult>,
    private readonly options: UploadQueueOptions<TResult> = {},
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ReadonlyArray<UploadItem<TResult>> => this.items;

  add = (files: FileList | File[]): void => {
    const next: Array<UploadItem<TResult>> = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: 'queued',
      progress: { loaded: 0, total: file.size, ratio: 0 },
    }));
    this.items = [...this.items, ...next];
    this.pending.push(...next.map((i) => i.id));
    this.emit();
    this.pump();
  };

  cancel = (id: string): void => {
    this.controllers.get(id)?.abort();
    this.dequeue(id);
    this.patch(id, (item) => (item.status === 'queued' ? { ...item, status: 'cancelled' } : item));
  };

  remove = (id: string): void => {
    this.controllers.get(id)?.abort();
    this.dequeue(id);
    this.items = this.items.filter((i) => i.id !== id);
    this.emit();
  };

  clearFinished = (): void => {
    this.items = this.items.filter((i) => i.status === 'queued' || i.status === 'uploading');
    this.emit();
  };

  private dequeue(id: string) {
    const index = this.pending.indexOf(id);
    if (index >= 0) this.pending.splice(index, 1);
  }

  private patch(id: string, update: (item: UploadItem<TResult>) => UploadItem<TResult>) {
    this.items = this.items.map((item) => (item.id === id ? update(item) : item));
    this.emit();
  }

  private emit() {
    for (const listener of this.listeners) listener();
  }

  private pump() {
    const concurrency = this.options.concurrency ?? 3;
    while (this.active < concurrency && this.pending.length > 0) {
      const id = this.pending.shift();
      const item = this.items.find((i) => i.id === id);
      if (item?.status === 'queued') this.start(item);
    }
  }

  private start(item: UploadItem<TResult>) {
    const controller = new AbortController();
    this.controllers.set(item.id, controller);
    this.active += 1;
    this.patch(item.id, (i) => ({ ...i, status: 'uploading' }));
    void this.uploader
      .upload(item.file, {
        signal: controller.signal,
        onProgress: (progress) => this.patch(item.id, (i) => ({ ...i, progress })),
      })
      .then((result) => {
        let done: UploadItem<TResult> | undefined;
        this.patch(item.id, (i) => {
          done = {
            ...i,
            status: 'done',
            result,
            progress: { loaded: i.file.size, total: i.file.size, ratio: 1 },
          };
          return done;
        });
        if (done) this.options.onDone?.(result, done);
      })
      .catch((error: unknown) => {
        this.patch(item.id, (i) => ({
          ...i,
          status: controller.signal.aborted ? 'cancelled' : 'error',
          error,
        }));
      })
      .finally(() => {
        this.active -= 1;
        this.controllers.delete(item.id);
        this.pump();
      });
  }
}

/** Queue of uploads for a media picker: add files, watch progress, cancel or remove. */
export function useUploads<TPresign extends Presigned, TResult>(
  uploader: Uploader<TPresign, TResult>,
  options: UploadQueueOptions<TResult> = {},
) {
  const { concurrency, onDone } = options;
  const queue = useMemo(
    () =>
      new UploadQueue(uploader, {
        ...(concurrency ? { concurrency } : {}),
        ...(onDone ? { onDone } : {}),
      }),
    [uploader, concurrency, onDone],
  );
  const items = useSyncExternalStore(queue.subscribe, queue.getSnapshot, queue.getSnapshot);
  return {
    items,
    add: queue.add,
    cancel: queue.cancel,
    remove: queue.remove,
    clearFinished: queue.clearFinished,
  };
}
